import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { BUYING_TIPS, TIP_ORDER } from "@/data/buyingTipsContent";

const SITE = "https://lagooncityrealestate.ca";

const TIPS = TIP_ORDER.map((slug) => ({
  slug,
  title: BUYING_TIPS[slug].title,
  img: BUYING_TIPS[slug].img,
}));

export default function BuyingTips() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = `${SITE}/buying-tips`;
  const title = "Buying Tips — Your Guide to Buying a Home in Lagoon City | Joselle Alves";
  const desc =
    "Everything you need to buy with confidence in Lagoon City: a 12-step buying guide, land transfer tax, government programs, checklists, title insurance and more — from broker Joselle Alves.";

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <PageHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="/assets/community/lagoon-city-main.webp"
            alt="Aerial view of Lagoon City waterfront homes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/85 to-[#0D0B0E]/60" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-16 sm:pt-32 sm:pb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4">Buying Tips</p>
            <h1 data-testid="buying-tips-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
              Buy your Lagoon City home with <em className="italic text-[#E890A7]">confidence</em>
            </h1>
            <p className="mt-6 max-w-2xl text-slate-300 font-light text-lg">
              From your very first step to closing day, here are the guides, checklists and details that make
              buying a home clear and stress-free — put together for you by Joselle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tiles */}
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="buying-tips-grid">
          {TIPS.map((t, i) => (
            <motion.div
              key={t.title}
              data-testid={`buying-tip-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
            >
              <Link
                to={`/buying-tips/${t.slug}`}
                className="group relative flex flex-col rounded-3xl border border-[#E890A7]/15 bg-[#151218]/70 p-6 hover:border-[#E890A7]/45 hover:-translate-y-1 transition-all duration-300 h-full"
              >
                <div className="h-44 flex items-center justify-center overflow-hidden">
                  <img
                    src={t.img}
                    alt={t.title}
                    className="h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <h2 className="font-display text-xl leading-snug">{t.title}</h2>
                  <ArrowUpRight size={18} className="mt-1 shrink-0 text-[#E890A7]/70 group-hover:text-[#F2A7BA] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-12">
          <h2 className="font-display font-light text-3xl sm:text-4xl leading-tight max-w-2xl">
            Ready to start your Lagoon City home search?
          </h2>
          <p className="mt-4 max-w-2xl text-slate-400 font-light">
            Have a question these guides didn&apos;t answer? Joselle is happy to walk you through it, no obligation.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              data-testid="buying-tips-cta-contact"
              to="/#contact"
              className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
            >
              Talk to Joselle <ArrowRight size={16} />
            </Link>
            <Link
              data-testid="buying-tips-cta-listings"
              to="/#properties"
              className="rounded-full border border-[#E890A7]/40 text-[#F2A7BA] px-7 py-3.5 flex items-center gap-2 hover:bg-[#E890A7]/10 transition-all duration-300"
            >
              Browse waterfront homes <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
